import React, { useEffect, useState } from 'react';
import { Card, Space, Typography, Input, Button, List, Spin, Tooltip, Alert, Modal, Collapse, Tag } from 'antd';
import { RobotOutlined, SendOutlined, ClearOutlined, HistoryOutlined, QuestionCircleOutlined, FileTextOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import { DIFY_CONFIG } from '../config/dify';
import { qaHistoryService, SimilarQuestion } from '../services/qaHistoryService';
import QAHistory from './QAHistory';
import SourceReferences from './SourceReferences';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import '../styles/markdown.css';

const { Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface Source {
  id: string;
  name: string;
  content: string;
  score: number;
  segment_id?: string;
  url?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: Source[];
  has_sources?: boolean;
  response_type?: 'knowledge_based' | 'generated';
}

interface DifyChatSDKProps {
  height?: string;
  showHeader?: boolean;
}

/**
 * Dify 聊天机器人组件 - 使用 API 直接调用
 * 这种方式完全控制 UI 和交互，避免 iframe 的跨域问题
 */
const DifyChatSDK: React.FC<DifyChatSDKProps> = ({ 
  height = '700px',
  showHeader = true 
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome-' + Date.now(),
      role: 'assistant',
      content: '您好！我是主数据与API技术顾问，专门回答业务主数据在系统中的接口信息、表结构、字段定义及注释说明。\n\n## 📊 专业领域\n\n### 🗄️ 数据库表结构\n- 主数据表设计与字段定义\n- 表关系与外键约束说明\n- 字段类型、长度、默认值\n- 业务字段注释与说明文档\n\n### 🔌 API 接口文档\n- RESTful API 接口规范\n- 请求参数与响应格式\n- 接口调用示例与说明\n- 错误码定义与处理\n\n### 🚢 海运物流主数据\n- 海运舱单数据结构详解\n- 订舱、提单、货物数据字段\n- 集装箱、航次、船公司信息\n- 港口、承运商、代理商数据\n\n### 👥 客户主数据体系\n- 客户基础信息表结构\n- 客户层级关系与分类\n- 联系人、地址、银行账户\n- 信用额度、业务范围配置\n\n### 💼 业务系统数据\n- TMS运输管理系统数据\n- 计费财务系统表结构\n- 关务申报数据字典\n- HR人事系统数据模型\n\n我可以为您提供具体的表结构、字段说明、API接口文档等技术信息。请问您需要了解哪个方面的数据结构？',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [similarQuestions, setSimilarQuestions] = useState<SimilarQuestion[]>([]);
  const [showSimilarQuestions, setShowSimilarQuestions] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  
  // 生成或获取用户ID，保持整个会话期间一致
  const [userId] = useState(() => {
    const storedUserId = localStorage.getItem('dify-user-id');
    if (storedUserId) {
      return storedUserId;
    }
    const newUserId = 'user-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('dify-user-id', newUserId);
    return newUserId;
  });

  // Markdown渲染组件配置
  const markdownComponents = {
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      if (!inline && language) {
        return (
          <SyntaxHighlighter
            style={vscDarkPlus}
            language={language}
            PreTag="div"
            customStyle={{
              margin: '8px 0',
              borderRadius: '6px',
              fontSize: '13px'
            }}
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        );
      }
      
      return (
        <code 
          style={{
            padding: '2px 6px',
            margin: '0 2px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e1e4e8',
            borderRadius: '3px',
            fontSize: '0.9em',
            color: '#e01e5a'
          }}
          {...props}
        >
          {children}
        </code>
      );
    },
    table({ children }: any) {
      return (
        <div style={{ overflowX: 'auto', margin: '12px 0' }}>
          <table style={{
            borderCollapse: 'collapse',
            width: '100%',
            border: '1px solid #d9d9d9'
          }}>
            {children}
          </table>
        </div>
      );
    },
    th({ children }: any) {
      return (
        <th style={{
          padding: '10px 12px',
          backgroundColor: '#fafafa',
          border: '1px solid #d9d9d9',
          fontWeight: 600,
          textAlign: 'left'
        }}>
          {children}
        </th>
      );
    },
    td({ children }: any) {
      return (
        <td style={{
          padding: '10px 12px',
          border: '1px solid #d9d9d9'
        }}>
          {children}
        </td>
      );
    },
    blockquote({ children }: any) {
      return (
        <blockquote style={{
          margin: '12px 0',
          padding: '12px 20px',
          borderLeft: '4px solid #1890ff',
          backgroundColor: '#f6f8fa',
          color: '#666'
        }}>
          {children}
        </blockquote>
      );
    },
    h1({ children }: any) {
      return <h1 style={{ fontSize: '24px', fontWeight: 600, margin: '16px 0 12px', color: '#000' }}>{children}</h1>;
    },
    h2({ children }: any) {
      return <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '14px 0 10px', color: '#000' }}>{children}</h2>;
    },
    h3({ children }: any) {
      return <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '12px 0 8px', color: '#000' }}>{children}</h3>;
    },
    ul({ children }: any) {
      return <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ul>;
    },
    ol({ children }: any) {
      return <ol style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ol>;
    },
    li({ children }: any) {
      return <li style={{ margin: '4px 0' }}>{children}</li>;
    },
    p({ children }: any) {
      return <p style={{ margin: '8px 0', lineHeight: 1.6 }}>{children}</p>;
    },
    a({ href, children }: any) {
      return (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#1890ff', textDecoration: 'none' }}
        >
          {children}
        </a>
      );
    }
  };

  // 格式化助手消息，支持 Markdown 和表格（保留旧函数以防需要）
  const formatAssistantMessage = (content: string): string => {
    let formatted = content;
    
    // 1. 先处理多行代码块（```），避免内部内容被其他规则处理
    formatted = formatted.replace(/```([\s\S]*?)```/g, (match, code) => {
      return `<pre style="background: #f5f5f5; padding: 12px; border-radius: 4px; overflow-x: auto; margin: 8px 0;"><code style="font-family: monospace; font-size: 13px;">${code.trim()}</code></pre>`;
    });
    
    // 2. 处理表格 - 改进版，支持多行表格
    const tableRegex = /(\|.+\|[\r\n]+)+((\|[\s\-:]+\|[\r\n]+)?(\|.+\|[\r\n]*)+)?/g;
    formatted = formatted.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n').filter(line => line.trim());
      if (lines.length === 0) return match;
      
      // 检查是否有分隔行
      const hasSeparator = lines.some(line => /^\|[\s\-:]+\|$/.test(line.trim()));
      const dataLines = lines.filter(line => !/^\|[\s\-:]+\|$/.test(line.trim()));
      
      if (dataLines.length === 0) return match;
      
      // 构建表格HTML
      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 14px;">';
      
      // 如果有分隔行，第一行是表头
      if (hasSeparator && dataLines.length > 0) {
        // 表头
        const headerCells = dataLines[0].split('|').filter(cell => cell.trim());
        tableHtml += '<thead><tr style="background: #fafafa;">';
        headerCells.forEach(cell => {
          tableHtml += `<th style="border: 1px solid #e8e8e8; padding: 8px 12px; text-align: left; font-weight: 600;">${cell.trim()}</th>`;
        });
        tableHtml += '</tr></thead>';
        
        // 表体
        if (dataLines.length > 1) {
          tableHtml += '<tbody>';
          for (let i = 1; i < dataLines.length; i++) {
            const cells = dataLines[i].split('|').filter(cell => cell.trim());
            tableHtml += '<tr>';
            cells.forEach(cell => {
              tableHtml += `<td style="border: 1px solid #e8e8e8; padding: 8px 12px;">${cell.trim()}</td>`;
            });
            tableHtml += '</tr>';
          }
          tableHtml += '</tbody>';
        }
      } else {
        // 没有分隔行，所有行都是数据行
        tableHtml += '<tbody>';
        dataLines.forEach((line, index) => {
          const cells = line.split('|').filter(cell => cell.trim());
          tableHtml += `<tr style="${index % 2 === 0 ? 'background: #fafafa;' : ''}">`;
          cells.forEach(cell => {
            tableHtml += `<td style="border: 1px solid #e8e8e8; padding: 8px 12px;">${cell.trim()}</td>`;
          });
          tableHtml += '</tr>';
        });
        tableHtml += '</tbody>';
      }
      
      tableHtml += '</table>';
      return tableHtml;
    });
    
    // 3. 处理标题（确保标题在独立行）
    formatted = formatted.replace(/^#{1,6}\s+(.+)$/gm, (match, title) => {
      const level = match.match(/^#+/)?.[0]?.length || 1;
      const styles: Record<number, string> = {
        1: 'font-size: 24px; font-weight: 600; margin: 20px 0 12px 0; color: #1890ff;',
        2: 'font-size: 20px; font-weight: 600; margin: 16px 0 10px 0; color: #1890ff;',
        3: 'font-size: 18px; font-weight: 600; margin: 12px 0 8px 0; color: #1890ff;',
        4: 'font-size: 16px; font-weight: 600; margin: 10px 0 6px 0; color: #1890ff;',
        5: 'font-size: 14px; font-weight: 600; margin: 8px 0 4px 0; color: #1890ff;',
        6: 'font-size: 14px; font-weight: 600; margin: 8px 0 4px 0; color: #666;'
      };
      return `<h${level} style="${styles[level] || styles[6]}">${title.trim()}</h${level}>`;
    });
    
    // 4. 处理列表
    // 无序列表
    formatted = formatted.replace(/^[\*\-]\s+(.+)$/gm, '<li style="margin: 4px 0;">$1</li>');
    // 有序列表
    formatted = formatted.replace(/^\d+\.\s+(.+)$/gm, '<li style="margin: 4px 0;">$1</li>');
    
    // 将连续的列表项包装在 ul/ol 中
    formatted = formatted.replace(/(<li[^>]*>.*?<\/li>\s*)+/g, (match) => {
      return `<ul style="margin: 8px 0; padding-left: 24px; list-style-type: disc;">${match}</ul>`;
    });
    
    // 5. 处理粗体和斜体
    formatted = formatted.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // 6. 处理行内代码（在处理完代码块后）
    formatted = formatted.replace(/`([^`]+)`/g, '<code style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 13px; color: #d14;">$1</code>');
    
    // 7. 处理链接
    formatted = formatted.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" target="_blank" style="color: #1890ff; text-decoration: underline;">$1</a>');
    
    // 8. 处理分割线
    formatted = formatted.replace(/^[\*\-\_]{3,}$/gm, '<hr style="margin: 16px 0; border: none; border-top: 1px solid #e8e8e8;">');
    
    // 9. 处理换行（段落）
    formatted = formatted.split('\n\n').map(paragraph => {
      // 如果段落已经是HTML元素，不添加额外的包装
      if (paragraph.trim().match(/^<(h[1-6]|ul|ol|table|pre|hr)/)) {
        return paragraph;
      }
      // 普通段落
      return paragraph.trim() ? `<p style="margin: 8px 0; line-height: 1.6;">${paragraph.trim()}</p>` : '';
    }).join('');
    
    // 10. 处理单个换行（在段落内）
    formatted = formatted.replace(/([^>])\n([^<])/g, '$1<br/>$2');
    
    return formatted;
  };

  // 检查输入内容的相似问题
  const checkSimilarQuestions = (input: string) => {
    if (input.length < 2) {
      setSimilarQuestions([]);
      setShowSimilarQuestions(false);
      return;
    }

    const similar = qaHistoryService.findSimilarQuestions(input, 3);
    setSimilarQuestions(similar);
    setShowSimilarQuestions(similar.length > 0);
  };


  // 发送消息到 Dify API
  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    console.log('📤 发送消息:', {
      message: inputValue,
      currentConversationId: conversationId,
      messagesCount: messages.length,
      userId: userId
    });

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      // 调用 Dify Chat API
      const response = await fetch(`${DIFY_CONFIG.BASE_URL}/v1/chat-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DIFY_CONFIG.API_KEY}`
        },
        body: JSON.stringify({
          inputs: {},
          query: inputValue,
          conversation_id: conversationId,
          response_mode: 'blocking',
          user: userId
        })
      });

      if (!response.ok) {
        throw new Error(`API 请求失败: ${response.status}`);
      }

      const data = await response.json();
      
      // 更新会话 ID - 始终更新，不只是第一次
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
        console.log('✅ 会话ID已更新:', {
          oldId: conversationId,
          newId: data.conversation_id,
          isNewConversation: !conversationId
        });
      }

      // 添加 AI 响应
      const assistantMessage: Message = {
        id: data.id || Date.now().toString(),
        role: 'assistant',
        content: data.answer || '抱歉，我没有理解您的问题。',
        timestamp: new Date(),
        sources: data.sources || [],
        has_sources: data.has_sources || false,
        response_type: data.metadata?.response_type || 'generated'
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // 保存到历史记录
      qaHistoryService.addRecord(userMessage.content, data.answer || '抱歉，我没有理解您的问题。', data.conversation_id);
    } catch (error) {
      console.error('发送消息失败:', error);
      
      // 如果是 CORS 错误，使用代理方式
      try {
        // 通过后端代理调用
        const proxyResponse = await fetch('/api/dify/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: inputValue,
            conversationId: conversationId
          })
        });

        if (proxyResponse.ok) {
          const data = await proxyResponse.json();
          
          // 更新会话ID（如果通过代理返回）
          if (data.conversation_id) {
            setConversationId(data.conversation_id);
            console.log('通过代理更新会话ID:', data.conversation_id);
          }
          
          const assistantMessage: Message = {
            id: Date.now().toString(),
            role: 'assistant',
            content: data.answer || '抱歉，服务暂时不可用。',
            timestamp: new Date(),
            sources: data.sources || [],
            has_sources: data.has_sources || false,
            response_type: data.metadata?.response_type || 'generated'
          };
          setMessages(prev => [...prev, assistantMessage]);
          
          // 保存到历史记录
          qaHistoryService.addRecord(userMessage.content, data.answer || '抱歉，服务暂时不可用。', data.conversation_id);
        }
      } catch (proxyError) {
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: '抱歉，服务暂时不可用。请稍后再试。',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
    }
  };

  // 重置对话
  const resetConversation = () => {
    // 保留欢迎消息
    setMessages([{
      id: 'welcome-' + Date.now(),
      role: 'assistant',
      content: '您好！我是主数据与API技术顾问，专门回答业务主数据在系统中的接口信息、表结构、字段定义及注释说明。\n\n## 📊 专业领域\n\n### 🗄️ 数据库表结构\n- 主数据表设计与字段定义\n- 表关系与外键约束说明\n- 字段类型、长度、默认值\n- 业务字段注释与说明文档\n\n### 🔌 API 接口文档\n- RESTful API 接口规范\n- 请求参数与响应格式\n- 接口调用示例与说明\n- 错误码定义与处理\n\n### 🚢 海运物流主数据\n- 海运舱单数据结构详解\n- 订舱、提单、货物数据字段\n- 集装箱、航次、船公司信息\n- 港口、承运商、代理商数据\n\n### 👥 客户主数据体系\n- 客户基础信息表结构\n- 客户层级关系与分类\n- 联系人、地址、银行账户\n- 信用额度、业务范围配置\n\n### 💼 业务系统数据\n- TMS运输管理系统数据\n- 计费财务系统表结构\n- 关务申报数据字典\n- HR人事系统数据模型\n\n我可以为您提供具体的表结构、字段说明、API接口文档等技术信息。请问您需要了解哪个方面的数据结构？',
      timestamp: new Date()
    }]);
    setConversationId(null);  // 清除会话ID，开始新会话
    setInputValue('');
    console.log('会话已重置，userId保持不变:', userId);
  };

  const content = (
    <div style={{ height, display: 'flex', flexDirection: 'column' }}>
      {/* 消息列表 */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '16px',
        backgroundColor: '#f5f5f5'
      }}>
        <List
          dataSource={messages}
          renderItem={(message) => (
            <div style={{ 
              marginBottom: '16px',
              textAlign: message.role === 'user' ? 'right' : 'left'
            }}>
              <div style={{
                display: 'inline-block',
                maxWidth: message.role === 'assistant' && message.has_sources ? '95%' : '85%',
                width: message.role === 'assistant' && message.has_sources ? '100%' : 'auto'
              }}>
                <div style={{
                  padding: message.role === 'user' ? '8px 16px' : '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: message.role === 'user' ? '#1890ff' : '#fff',
                  color: message.role === 'user' ? '#fff' : '#000',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: message.role === 'assistant' ? '1px solid #f0f0f0' : 'none'
                }}>
                  {message.role === 'assistant' ? (
                    <div 
                      className="markdown-viewer"
                      style={{ 
                        lineHeight: '1.6',
                        fontSize: '14px'
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <span style={{ fontSize: '14px' }}>{message.content}</span>
                  )}
                </div>
                
                {/* 显示引用来源 - 仅对助手消息 */}
                {message.role === 'assistant' && (
                  <SourceReferences 
                    sources={message.sources || []}
                    response_type={message.response_type}
                  />
                )}
                
                <div style={{ 
                  fontSize: '12px', 
                  color: '#999', 
                  marginTop: '4px',
                  textAlign: message.role === 'user' ? 'right' : 'left'
                }}>
                  {message.timestamp.toLocaleTimeString()}
                  {message.role === 'assistant' && message.has_sources && (
                    <Tag 
                      color="green" 
                      style={{ marginLeft: 8, fontSize: '11px', padding: '1px 6px' }}
                    >
                      已验证
                    </Tag>
                  )}
                  {message.role === 'assistant' && message.response_type === 'generated' && (
                    <Tag 
                      color="orange" 
                      style={{ marginLeft: 8, fontSize: '11px', padding: '1px 6px' }}
                    >
                      AI生成
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          )}
        />
        {loading && (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Spin tip="思考中..." />
          </div>
        )}
      </div>

      {/* 输入区域 */}
      <div style={{ 
        padding: '16px', 
        borderTop: '1px solid #f0f0f0',
        backgroundColor: '#fff',
        position: 'relative'
      }}>
        {/* 相似问题提示 */}
        {showSimilarQuestions && similarQuestions.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '16px',
            right: '16px',
            backgroundColor: '#fff',
            border: '1px solid #f0f0f0',
            borderRadius: '8px',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            padding: '8px',
            marginBottom: '8px',
            zIndex: 10
          }}>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <QuestionCircleOutlined />
              相似的历史问题：
            </div>
            {similarQuestions.map((sq, index) => (
              <div 
                key={index}
                style={{
                  padding: '8px',
                  marginBottom: '4px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s'
                }}
                onClick={() => {
                  setInputValue(sq.record.question);
                  setShowSimilarQuestions(false);
                  
                  // 直接显示历史答案
                  const historicalAnswer: Message = {
                    id: 'historical-' + Date.now(),
                    role: 'assistant',
                    content: `【来自历史记录】\n\n${sq.record.answer}`,
                    timestamp: new Date()
                  };
                  setMessages(prev => [...prev, historicalAnswer]);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e6f7ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
              >
                <div style={{ 
                  color: '#1890ff',
                  marginBottom: '4px',
                  fontWeight: 500
                }}>
                  {sq.record.question}
                </div>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#999',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>相似度: {(sq.similarity * 100).toFixed(0)}%</span>
                  <span>{new Date(sq.record.timestamp).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              checkSimilarQuestions(e.target.value);
            }}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="请输入您的问题..."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading}
          />
          <Button 
            type="primary" 
            icon={<SendOutlined />}
            onClick={sendMessage}
            disabled={loading || !inputValue.trim()}
          >
            发送
          </Button>
        </Space.Compact>
      </div>
    </div>
  );

  if (!showHeader) {
    return content;
  }

  return (
    <Card 
      title={
        <Space>
          <RobotOutlined /> 
          <Text>主数据与API智能顾问</Text>
        </Space>
      }
      extra={
        <Space>
          <Tooltip title="查看历史记录">
            <Button 
              icon={<HistoryOutlined />}
              onClick={() => setHistoryModalVisible(true)}
              size="small"
            >
              历史记录
            </Button>
          </Tooltip>
          <Tooltip title="重置对话">
            <Button 
              icon={<ClearOutlined />}
              onClick={resetConversation}
              size="small"
              disabled={loading}
            >
              重置
            </Button>
          </Tooltip>
        </Space>
      }
      styles={{ body: { padding: 0 } }}
    >
      {content}
      
      {/* 历史记录弹窗 */}
      <Modal
        title="问答历史记录"
        visible={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={null}
        width={900}
        bodyStyle={{ padding: 0, maxHeight: '70vh', overflow: 'auto' }}
      >
        <QAHistory />
      </Modal>
    </Card>
  );
};

export default DifyChatSDK;